import { z } from "zod";
import { ALL_STATUS_VALUES } from '../constants';
import {
  CUSTOMER_FACING_DESCRIPTION_MAX,
  CUSTOMER_FACING_NAME_MAX,
  PRO015_DESCRIPTION_MAX,
} from '../constants/campaign-name';
import {
  CAMPAIGN_SLUG_MAX,
  CAMPAIGN_SLUG_MIN,
  isCampaignSlug,
} from '../constants/campaign-slug';
import { SLOT_ICON_CATALOG } from '../constants/mechanic-defaults';
import {
  CAMPAIGN_LINK_COPY,
  FIELD_MESSAGES,
  FILE_MESSAGES,
  MECHANIC_RULE_MESSAGES,
} from '../constants/messages';
import { MAX_UPLOAD_BYTES } from '../constants/upload';
import { isPastDate, isStartDateBeforeEndDate } from '@/utils/date-string';
import { collectMechanicRefineIssues } from './mechanic-rules';
import { hasCoverImage, hasWideBanner } from '../utils/banner-source';
import { CampaignType, VoucherValidityMode } from "@/@types/gamification";

const storeSchema = z.object({
  storeCode: z.string().min(1, "Código é obrigatório"),
  storeName: z.string().min(1, "Endereço é obrigatório"),
  nickname: z.string().min(1, "Nome da loja é obrigatório"),
});

const allStoresSchema = z.object({
  storeCode: z.literal("0"),
  storeName: z.string(),
  nickname: z.string(),
});

const flexibleStoreSchema = z.union([storeSchema, allStoresSchema]);

const optionalNumber = z.preprocess(
  (val) => (val === "" || val === null || val === undefined ? undefined : Number(val)),
  z.number().optional(),
);

const numberOrZero = z.preprocess((val) => {
  if (val === null || val === undefined || val === "" || isNaN(Number(val))) {
    return 0;
  }
  return Number(val);
}, z.number());

const prizeRowSchema = z.object({
  value: numberOrZero.refine((v) => v >= 0, "Valor deve ser ≥ 0"),
  probability: numberOrZero
    .refine((v) => v >= 0, "Porcentagem deve ser ≥ 0")
    .refine((v) => v <= 100, "Porcentagem deve ser ≤ 100"),
  label: z.string().optional(),
});

const concurrencySchema = z
  .object({
    competes: z.boolean().default(false),
    group: z.string().nullish(),
  })
  .optional();

const voucherValiditySchema = z
  .object({
    mode: z.nativeEnum(VoucherValidityMode).optional(),
    validityDays: optionalNumber,
    startOffsetDays: optionalNumber,
  })
  .optional();

const cpfSegmentationSchema = z
  .object({
    enabled: z.boolean().default(false),
  })
  .optional();

const isSlotIcon = (value: string): boolean =>
  value === "" || SLOT_ICON_CATALOG.some((icon) => icon.value === value);

const slotSymbolSchema = z.object({
  id: z.string().min(1, "ID do símbolo é obrigatório"),
  label: z.string().min(1, "Rótulo é obrigatório"),
  value: z.preprocess((v) => Number(v ?? 0), z.number().min(0, "Valor deve ser ≥ 0")),
  icon: z
    .string()
    .refine(isSlotIcon, MECHANIC_RULE_MESSAGES.slotSymbolIcon)
    .optional(),
});

const campaignSlugSchema = z
  .string()
  .trim()
  .refine(
    (value) => value === "" || isCampaignSlug(value),
    FIELD_MESSAGES.slugFormat(CAMPAIGN_SLUG_MIN, CAMPAIGN_SLUG_MAX),
  )
  .optional();

const requiredCampaignSlugSchema = z
  .string({ required_error: CAMPAIGN_LINK_COPY.required })
  .trim()
  .min(1, CAMPAIGN_LINK_COPY.required)
  .refine(
    isCampaignSlug,
    FIELD_MESSAGES.slugFormat(CAMPAIGN_SLUG_MIN, CAMPAIGN_SLUG_MAX),
  );

const presentationSchema = z
  .object({
    customerFacingName: z
      .string()
      .trim()
      .max(
        CUSTOMER_FACING_NAME_MAX,
        FIELD_MESSAGES.customerFacingNameMax(CUSTOMER_FACING_NAME_MAX),
      )
      .optional(),
    customerFacingDescription: z
      .string()
      .trim()
      .max(
        CUSTOMER_FACING_DESCRIPTION_MAX,
        FIELD_MESSAGES.customerFacingDescriptionMax(
          CUSTOMER_FACING_DESCRIPTION_MAX,
        ),
      )
      .optional(),
  })
  .optional();

const isImageFile = (file: unknown): boolean =>
  !(file instanceof File) || file.type.startsWith("image/");

const fitsUploadLimit = (file: unknown): boolean =>
  !(file instanceof File) || file.size <= MAX_UPLOAD_BYTES;

const optionalBannerSchema = (fileMessage: string, imageMessage: string) =>
  z
    .any()
    .optional()
    .refine((file) => !file || file instanceof File, fileMessage)
    .refine(isImageFile, imageMessage)
    .refine(fitsUploadLimit, FILE_MESSAGES.imageMaxSize);

const bannerSourceKeySchema = z.string().trim().min(1).optional();

const bannerSourcePreviewSchema = z
  .object({ url: z.string(), label: z.string() })
  .optional();

const bannerSourceFields = {
  banner16x9SourceKey: bannerSourceKeySchema,
  banner16x9SourcePreview: bannerSourcePreviewSchema,
  banner3x4SourceKey: bannerSourceKeySchema,
  banner3x4SourcePreview: bannerSourcePreviewSchema,
};

const faqItemSchema = z.object({
  question: z.string().min(1, "Pergunta é obrigatória"),
  answer: z.string().min(1, "Resposta é obrigatória"),
});

const mechanicConfigSchema = z.object({
  prizes: z.array(prizeRowSchema).optional(),
  maxPlays: optionalNumber,
  minPurchase: optionalNumber,
  priority: optionalNumber,
  concurrency: concurrencySchema,
  voucherValidity: voucherValiditySchema,
  cpfSegmentation: cpfSegmentationSchema,
  mechanicParams: z
    .object({
      goalProbability: optionalNumber,
      symbols: z.array(slotSymbolSchema).optional(),
    })
    .optional(),
  presentation: presentationSchema,
}).optional();

const faqItemsSchema = z.array(faqItemSchema).optional();

export { prizesSumIsValid } from './mechanic-rules';

interface RefinableValues {
  campaignType?: CampaignType;
  campaignName?: string;
  mechanicConfig?: z.infer<typeof mechanicConfigSchema>;
  faqItems?: Array<{ question?: string; answer?: string }>;
}

function refineByMechanic(data: RefinableValues, ctx: z.RefinementCtx) {
  for (const issue of collectMechanicRefineIssues(data)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: issue.path,
      message: issue.message,
    });
  }
}

const baseGamificationSchema = z.object({

  campaignName: z.string()
    .min(1, FIELD_MESSAGES.campaignNameRequired)
    .max(PRO015_DESCRIPTION_MAX, FIELD_MESSAGES.campaignNameMax(PRO015_DESCRIPTION_MAX)),

  slug: requiredCampaignSlugSchema,

  campaignType: z.nativeEnum(CampaignType).default(CampaignType.ROULETTE),

  startDate: z.string()
    .min(1, FIELD_MESSAGES.startDateRequired),

  endDate: z.string()
    .min(1, FIELD_MESSAGES.endDateRequired),

  stores: z.array(flexibleStoreSchema)
    .min(1, FIELD_MESSAGES.storesMin)
    .refine((stores) => {
      const codes = stores.map(store => store.storeCode);
      return new Set(codes).size === codes.length;
    }, FIELD_MESSAGES.storesUnique),

  allStoresSelected: z.boolean().optional(),

  mechanicConfig: mechanicConfigSchema,

  faqItems: faqItemsSchema,

  segmentationFile: z.instanceof(File).optional(),

  segmentationCpfs: z.array(z.string()).optional(),

  regulations: z.instanceof(File)
    .optional()
    .refine((file) => {
      if (!file) return true;
      return file.type === "application/pdf";
    }, FILE_MESSAGES.regulationPdf)
    .refine((file) => {
      if (!file) return true;
      return file.size <= MAX_UPLOAD_BYTES;
    }, FILE_MESSAGES.regulationMaxSize),

  banner16x9: optionalBannerSchema(
    FILE_MESSAGES.banner16x9File,
    FILE_MESSAGES.banner16x9Image,
  ),

  banner3x4: optionalBannerSchema(
    FILE_MESSAGES.banner3x4File,
    FILE_MESSAGES.banner3x4Image,
  ),

  ...bannerSourceFields,
});

export const createGamificationSchema = baseGamificationSchema
  .superRefine(refineByMechanic)
  .refine((data) => isStartDateBeforeEndDate(data.startDate, data.endDate), {
    message: MECHANIC_RULE_MESSAGES.dateOrder,
    path: ["endDate"],
  })
  .refine((data) => !isPastDate(data.startDate), {
    message: MECHANIC_RULE_MESSAGES.startDatePast,
    path: ["startDate"],
  })
  .refine(hasCoverImage, {
    message: FILE_MESSAGES.banner3x4Required,
    path: ["banner3x4"],
  })
  .refine(hasWideBanner, {
    message: FILE_MESSAGES.banner16x9Required,
    path: ["banner16x9"],
  });

export const draftGamificationSchema = z.object({
  campaignName: z.string().min(1, FIELD_MESSAGES.campaignNameRequired),
  slug: campaignSlugSchema,
  campaignType: z.nativeEnum(CampaignType).default(CampaignType.ROULETTE),
  startDate: z.string().min(1, FIELD_MESSAGES.startDateRequired),
  endDate: z.string().min(1, FIELD_MESSAGES.endDateRequired),
}).refine((data) => isStartDateBeforeEndDate(data.startDate, data.endDate), {
  message: MECHANIC_RULE_MESSAGES.dateOrder,
  path: ["endDate"],
}).refine((data) => !isPastDate(data.startDate), {
  message: MECHANIC_RULE_MESSAGES.startDatePast,
  path: ["startDate"],
});

export const updateGamificationSchema = z.object({
  id: z.number().positive(FIELD_MESSAGES.campaignIdRequired),

  campaignName: z.string()
    .min(1, FIELD_MESSAGES.campaignNameRequired)
    .max(PRO015_DESCRIPTION_MAX, FIELD_MESSAGES.campaignNameMax(PRO015_DESCRIPTION_MAX))
    .optional(),

  slug: requiredCampaignSlugSchema,

  campaignType: z.nativeEnum(CampaignType).optional(),

  startDate: z.string()
    .min(1, FIELD_MESSAGES.startDateRequired)
    .optional(),

  endDate: z.string()
    .min(1, FIELD_MESSAGES.endDateRequired)
    .optional(),

  stores: z.array(flexibleStoreSchema)
    .min(1, FIELD_MESSAGES.storesMin)
    .refine((stores) => {
      const codes = stores.map(store => store.storeCode);
      return new Set(codes).size === codes.length;
    }, FIELD_MESSAGES.storesUnique)
    .optional(),

  allStoresSelected: z.boolean().optional(),

  mechanicConfig: mechanicConfigSchema,

  faqItems: faqItemsSchema,

  segmentationCpfs: z.array(z.string()).optional(),

  regulations: z.instanceof(File)
    .optional()
    .refine((file) => {
      if (!file) return true;
      return file.type === "application/pdf";
    }, FILE_MESSAGES.regulationPdf)
    .refine((file) => {
      if (!file) return true;
      return file.size <= MAX_UPLOAD_BYTES;
    }, FILE_MESSAGES.regulationMaxSize),

  banner3x4: optionalBannerSchema(
    FILE_MESSAGES.banner3x4File,
    FILE_MESSAGES.banner3x4Image,
  ),

  banner16x9: optionalBannerSchema(
    FILE_MESSAGES.banner16x9File,
    FILE_MESSAGES.banner16x9Image,
  ),

  ...bannerSourceFields,
})
  .superRefine((data, ctx) => {

    if (data.campaignType) refineByMechanic(data, ctx);
  })
  .refine((data) => {
    if (data.startDate && data.endDate) {
      return isStartDateBeforeEndDate(data.startDate, data.endDate);
    }
    return true;
  }, {
    message: MECHANIC_RULE_MESSAGES.dateOrder,
    path: ["endDate"],
  });

export const GamificationFiltersSchema = z.object({
  search: z.string().optional(),
  status: z.enum(ALL_STATUS_VALUES as [string, ...string[]]).optional(),
  period: z.object({
    start: z.string().optional(),
    end: z.string().optional(),
  }).optional(),
  storeName: z.string().optional(),
});

export type CreateGamificationFormData = z.infer<typeof createGamificationSchema>;
export type DraftGamificationFormData = z.infer<typeof draftGamificationSchema>;
export type UpdateGamificationFormData = z.infer<typeof updateGamificationSchema>;
export type GamificationFiltersData = z.infer<typeof GamificationFiltersSchema>;
